import React, { useState, useEffect } from "react";
import { Box, Stack } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import MultiSelect from "../../../components/commonComponents/Selectors/MultiSelect";
import ActionButton, { LightGreyButton } from "../../../components/commonComponents/Buttons/ActionButton";
import DisabledButton from "../../../components/commonComponents/Buttons/DisabledButton";
import { FilterAlt, Clear } from "@mui/icons-material";
import { updateStateData, deleteItems } from "../../../Store/actions";
import { canViewStoreOwner } from "../../../api_calls/userManagement";
import SingleSelect from "../../../components/commonComponents/Selectors/SingleSelect";

const StoreDetailFilter = ({ onApplyFilters, open }) => {
  const storeData = useSelector((state) => state.userSelections);
  const dispatch = useDispatch();

  // Local filter state for each filter type
  const [storeNameFilter, setStoreNameFilter] = useState([]);
  const [locationFilter, setLocationFilter] = useState([]);
  const [ownerFilter, setOwnerFilter] = useState([]);
  const [listedFilter, setListedFilter] = useState(null);


  // Full list of stores and filtered IDs from Redux
  const storeList = storeData.stores_list || [];
  const filteredIds = new Set((storeData.filtered_stores || []).map(s => s.id));

  // Only include stores that are present in filtered_stores
  const detailedStores = storeList.filter(store => filteredIds.has(store.id));

  // Options for MultiSelect components
  const storeOptions = detailedStores.map((s) => ({ id: s.id, label: s.name }));

  // Location options: deduplicated values from Street & No., ZIP Code, City, Region
  const locationOptions = (() => {
    const seen = new Set();
    const options = [];
    detailedStores.forEach((s) => {
      [s.address, s.zipCode, s.city, s.region?.name]
        .filter(Boolean)
        .forEach((val) => {
          if (!seen.has(val)) {
            seen.add(val);
            options.push({ id: val, label: val });
          }
        });
    });
    return options;
  })();

  // State for owner options
  const [ownerOptions, setOwnerOptions] = useState([]);

  // Both conditional filters mirror the matching column in StoreDetailSelection
  const showOwnerFilter = canViewStoreOwner();
  const showListedFilter = typeof storeData.listing_check_saved_for_campaign !== "undefined" ? storeData.listing_check_saved_for_campaign : false;

  // Dynamically build owner options based on detailedStores
  useEffect(() => {
    const uniqueOwners = Array.from(
      new Set(detailedStores.map((s) => s.company.id))
    ).map((id) => {
      const company = detailedStores.find((s) => s.company.id === id).company;
      return { id: company.id, label: company.name };
    });

    setOwnerOptions(uniqueOwners);
  }, [detailedStores]);

  useEffect(() => {
    clearFilters()
  }, [open])

  // Apply filters and update Redux
  const applyFilters = () => {
    const filters = {
      store_name: storeNameFilter,
      location: locationFilter,
      owner: ownerFilter,
      listed: listedFilter,
    };

    dispatch(
      updateStateData({
        store_list_filters: filters,
        store_list_filter_applied: true,
      })
    );

    onApplyFilters(filters);
  };

  // Clear filters and reset Redux state
  const clearFilters = () => {
    setStoreNameFilter([]);
    setLocationFilter([]);
    setOwnerFilter([]);
    setListedFilter(null);

    dispatch(deleteItems(["store_list_filters", "store_list_filter_applied"]));
    onApplyFilters({});
  };

  // Initialize filters from Redux on mount or update
  useEffect(() => {
    if (storeData.store_list_filters) {
      const f = storeData.store_list_filters;
      setStoreNameFilter(f.store_name || []);
      setLocationFilter(f.location || []);
      setOwnerFilter(f.owner || []);
    }
  }, [storeData.store_list_filters]);

  // Determine if any filters are currently applied
  const hasFilters = storeNameFilter.length || locationFilter.length || ownerFilter.length || listedFilter;

  return (
    <Box>
      <Stack direction="row" spacing={2} flexWrap="wrap">
        {/* MultiSelect for Store Name */}
        <MultiSelect
          label="Store Name"
          options={storeOptions}
          usedFor="filter"
          selectionCheck={(o, v) => o.id === v.id}
          defaultVal={storeNameFilter}
          onChangeCall={setStoreNameFilter}
        />

        {/* MultiSelect for Location (Street & No., ZIP Code, City, Region) */}
        <MultiSelect
          label="Location"
          options={locationOptions}
          usedFor="filter"
          selectionCheck={(o, v) => o.id === v.id}
          defaultVal={locationFilter}
          onChangeCall={setLocationFilter}
        />

        {/* MultiSelect for Owner; shown whenever the Owner column is shown */}
        {showOwnerFilter && (
          <MultiSelect
            label="Owner"
            options={ownerOptions}
            usedFor="filter"
            selectionCheck={(o, v) => o.id === v.id}
            defaultVal={ownerFilter}
            onChangeCall={setOwnerFilter}
          />
        )}

        {/* Single Select for listed; hidden if no listing check saved for campaign */}
        {showListedFilter && (
          <SingleSelect
            label={"Listed"}
            allowClearance={true}
            elemId={"listedFilterOptions"}
            enableFullWidth={false}
            options={[
              { label: "Yes", value: "yes" },
              { label: "No", value: "no" }
            ]}
            usedFor="filter"
            defaultVal={listedFilter}
            selectionCheck={(option, value) => option.value === value.value}
            onChangeCall={setListedFilter}
          />
        )}

        {/* Filter action buttons */}
        <Stack direction="row" spacing={2} alignItems="center">
          {hasFilters ? (
            <ActionButton onClick={applyFilters} label="Apply" icon={<FilterAlt />} />
          ) : (
            <DisabledButton label="Apply" icon={<FilterAlt />} />
          )}

          {storeData.store_list_filter_applied ? (
            <LightGreyButton onClick={clearFilters} label="Clear" icon={<Clear />} />
          ) : (
            <DisabledButton label="Clear" icon={<Clear />} />
          )}
        </Stack>
      </Stack>
      <Box height={20} />
    </Box>
  );
};

export default StoreDetailFilter;
